import YAML from "yaml"

import type { DashboardElement, DashboardSpec } from "../dashboard/catalog"

import { logger } from "@/lib/logger"

import {
  applyJsonPatch,
  JsonPatchError,
  parseJsonPointer,
  type JsonPatchOperation,
} from "./json-patch"
import { structuralShare } from "./spec-diff"
import { mergeDashboardYamlEdit } from "./spec-merge"
import { applyAllAutoFixableSpecIssues } from "./spec-fixers"
import { applyUnifiedDiff, UnifiedDiffError } from "./unified-diff"
import { dashboardElementSchema, dashboardSpecSchema } from "../dashboard/catalog"

/**
 * Extracts json-render dashboard spec from AI response markdown.
 * Looks for ```json-render-stream ... ``` blocks first (JSONL format),
 * then ```json-render ... ``` code blocks,
 * then ```json ... ``` blocks as fallback.
 * Returns the last valid spec found (so streaming updates work progressively).
 */
export function extractDashboardSpec(markdown: string): DashboardSpec | null {
  // Try json-render-stream (JSONL) blocks first
  const jsonlBlocks = extractCodeBlocks(markdown, "json-render-stream")
  for (let i = jsonlBlocks.length - 1; i >= 0; i--) {
    const spec = parseJsonlBlock(jsonlBlocks[i])
    if (spec) return spec
  }

  // Try json-render fenced blocks
  const jsonRenderBlocks = extractCodeBlocks(markdown, "json-render")
  for (let i = jsonRenderBlocks.length - 1; i >= 0; i--) {
    const spec = parseAndValidate(jsonRenderBlocks[i])
    if (spec) return spec
  }

  // Fallback: try json blocks that look like dashboard specs
  const jsonBlocks = extractCodeBlocks(markdown, "json")
  for (let i = jsonBlocks.length - 1; i >= 0; i--) {
    const spec = parseAndValidate(jsonBlocks[i])
    if (spec) return spec
  }

  return null
}

function extractCodeBlocks(markdown: string, language: string): string[] {
  const regex = new RegExp(`\`\`\`${language}\\s*\\n([\\s\\S]*?)(?:\`\`\`|$)`, "gi")
  const blocks: string[] = []
  let match: RegExpExecArray | null
  while ((match = regex.exec(markdown)) !== null) {
    if (match[1]) {
      blocks.push(match[1].trim())
    }
  }
  return blocks
}

export type IncrementalDashboardEditFormat = "yaml-edit" | "yaml-patch" | "yaml-diff"

function stableSerialize(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerialize(item)).join(",")}]`
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).sort(([left], [right]) =>
      left.localeCompare(right)
    )
    return `{${entries
      .map(([key, item]) => `${JSON.stringify(key)}:${stableSerialize(item)}`)
      .join(",")}}`
  }
  return typeof value === "string" ? JSON.stringify(value) : String(value)
}

/**
 * Returns a canonical identity for the last incremental dashboard edit of a
 * given format. Assistant response metadata and surrounding prose may settle
 * after completion, but the parsed operation remains the same and must only
 * be applied once because yaml-patch/yaml-diff edits are relative.
 */
export function getIncrementalDashboardEditFingerprint(
  markdown: string,
  format: IncrementalDashboardEditFormat
): string | null {
  const blocks = extractCodeBlocks(markdown, format)
  if (blocks.length === 0) return null

  const block = blocks[blocks.length - 1].replace(/\r\n?/g, "\n").trim()
  if (format === "yaml-diff") return `${format}\u0000${block}`

  try {
    return `${format}\u0000${stableSerialize(YAML.parse(block))}`
  } catch {
    // The edit applicator will reject malformed YAML. Keeping a deterministic
    // raw fallback still makes this helper safe for callers and diagnostics.
    return `${format}\u0000${block}`
  }
}

function parseAndValidate(jsonStr: string): DashboardSpec | null {
  try {
    const parsed = JSON.parse(jsonStr)
    const result = dashboardSpecSchema.safeParse(parsed)
    if (result.success) {
      return result.data
    }
  } catch (error) {
    // Not valid JSON yet (may be streaming), or schema validation failed
    if (jsonStr.length > 0 && jsonStr.startsWith("{") && jsonStr.endsWith("}")) {
      logger.warn("spec-parser: parseAndValidate failed for complete JSON block", error as Error)
    }
  }
  return null
}

/**
 * Attempts to parse a partially-streamed JSON string.
 * Tries to close unclosed braces/brackets to make it valid JSON,
 * then validates against the dashboard schema.
 */
export function extractPartialDashboardSpec(markdown: string): DashboardSpec | null {
  // First try exact extraction
  const exact = extractDashboardSpec(markdown)
  if (exact) return exact

  // Try partial: find json-render block that might still be streaming
  const match = markdown.match(/```json-render\s*\n([\s\S]*?)$/i)
  if (!match?.[1]) return null

  const partial = match[1].trim()
  if (!partial.startsWith("{")) return null

  // Try to close the JSON
  const closed = tryCloseJson(partial)
  if (!closed) return null

  return parseAndValidate(closed)
}

/**
 * Parses a complete JSONL block where:
 * - Line 0 is the header (e.g. {"title": "..."})
 * - Lines 1+ are DashboardElements appended to children
 */
function parseJsonlBlock(blockContent: string): DashboardSpec | null {
  const lines = blockContent.split("\n").filter((l) => l.trim().length > 0)
  if (lines.length === 0) return null

  // Parse header (line 0)
  let header: { title?: string }
  try {
    header = JSON.parse(lines[0]) as { title?: string }
  } catch {
    return null
  }

  // Parse element lines
  const children: DashboardElement[] = []
  for (let i = 1; i < lines.length; i++) {
    try {
      const parsed: unknown = JSON.parse(lines[i])
      const result = dashboardElementSchema.safeParse(parsed)
      if (result.success) {
        children.push(result.data as DashboardElement)
      } else {
        logger.warn(`spec-parser: JSONL line ${i} failed schema validation`)
      }
    } catch {
      logger.warn(`spec-parser: JSONL line ${i} is not valid JSON`)
    }
  }

  if (children.length === 0) return null

  const spec: DashboardSpec = { title: header.title, children }
  return dashboardSpecSchema.safeParse(spec).success ? spec : null
}

// ─── JSONL Stream Compiler ──────────────────────────────────────────

export interface DashboardStreamCompiler {
  /** Feed updated markdown content; returns assembled spec or null */
  update(markdown: string): DashboardSpec | null
  /** Reset state for a new streaming session */
  reset(): void
  /** Number of complete lines already committed (exposed for testing) */
  readonly processedLineCount: number
}

/**
 * Creates a stateful compiler that incrementally parses json-render-stream
 * blocks. Only new complete lines are parsed on each update() call.
 */
export function createDashboardStreamCompiler(): DashboardStreamCompiler {
  let header: { title?: string } | null = null
  let committedChildren: DashboardElement[] = []
  let processedLineCount = 0

  function reset(): void {
    header = null
    committedChildren = []
    processedLineCount = 0
  }

  function update(markdown: string): DashboardSpec | null {
    // Find json-render-stream block (open or closed)
    const match = markdown.match(/```json-render-stream\s*\n([\s\S]*?)(?:```|$)/i)
    if (!match?.[1]) return null

    const blockContent = match[1]
    const allLines = blockContent.split("\n")

    // All lines except the very last are "complete" (terminated by \n).
    // The last element from split may be a partial line still being streamed.
    const completeLines = allLines.slice(0, -1)
    const partialLine = allLines[allLines.length - 1]?.trim() ?? ""

    // Process new complete lines since last update
    for (let i = processedLineCount; i < completeLines.length; i++) {
      const line = completeLines[i].trim()
      if (line.length === 0) continue

      if (i === 0) {
        // Header line
        try {
          header = JSON.parse(line) as { title?: string }
        } catch {
          logger.warn("spec-parser: JSONL header line is not valid JSON")
        }
      } else {
        // Element line
        try {
          const parsed: unknown = JSON.parse(line)
          const result = dashboardElementSchema.safeParse(parsed)
          if (result.success) {
            committedChildren.push(result.data as DashboardElement)
          } else {
            logger.warn(`spec-parser: JSONL stream line ${i} failed schema validation`)
          }
        } catch {
          logger.warn(`spec-parser: JSONL stream line ${i} is not valid JSON`)
        }
      }
    }
    processedLineCount = completeLines.length

    // Build result: committed children + optional preview of partial line
    const children = [...committedChildren]

    if (partialLine.length > 0 && partialLine.startsWith("{")) {
      const closed = tryCloseJson(partialLine)
      if (closed) {
        try {
          const parsed: unknown = JSON.parse(closed)
          const result = dashboardElementSchema.safeParse(parsed)
          if (result.success) {
            children.push(result.data as DashboardElement)
          }
        } catch {
          // Preview failed — that's fine, skip it
        }
      }
    }

    if (children.length === 0) return null

    return { title: header?.title, children }
  }

  return {
    update,
    reset,
    get processedLineCount() {
      return processedLineCount
    },
  }
}

// ─── YAML Stream Compiler ───────────────────────────────────────────

export interface YamlDashboardCompiler {
  /** Feed updated markdown content; returns latest valid spec or null */
  update(yamlText: string): DashboardSpec | null
  /** Reset state for a new streaming session */
  reset(): void
}

/**
 * Creates a stateful compiler that incrementally parses yaml-render blocks.
 * YAML is parsed on every update (line-by-line streaming gives property-level
 * granularity, unlike JSONL which needs a whole element per line).
 */
export function createYamlDashboardCompiler(): YamlDashboardCompiler {
  let lastValidSpec: DashboardSpec | null = null

  function reset(): void {
    lastValidSpec = null
  }

  function update(markdown: string): DashboardSpec | null {
    const match = markdown.match(/```yaml-render\s*\n([\s\S]*?)(?:```|$)/i)
    if (!match?.[1]) return lastValidSpec

    const yamlText = match[1]

    try {
      const parsed: unknown = YAML.parse(yamlText)
      if (parsed && typeof parsed === "object") {
        const result = dashboardSpecSchema.safeParse(parsed)
        if (result.success) {
          const newSpec = result.data as DashboardSpec
          // Structural sharing: preserve references for unchanged subtrees
          // to minimize React re-renders during streaming
          lastValidSpec = lastValidSpec
            ? (structuralShare(lastValidSpec, newSpec) as DashboardSpec)
            : newSpec
        }
      }
    } catch {
      // Incomplete YAML during streaming — keep last valid spec
    }

    return lastValidSpec
  }

  return { update, reset }
}

/**
 * Extracts a complete yaml-render dashboard spec from markdown.
 * Used for non-streaming (completed) responses.
 */
export function extractYamlDashboardSpec(markdown: string): DashboardSpec | null {
  const blocks = extractCodeBlocks(markdown, "yaml-render")
  for (let i = blocks.length - 1; i >= 0; i--) {
    try {
      const parsed: unknown = YAML.parse(blocks[i])
      if (parsed && typeof parsed === "object") {
        const result = dashboardSpecSchema.safeParse(parsed)
        if (result.success) {
          return result.data as DashboardSpec
        }
      }
    } catch {
      logger.warn("spec-parser: yaml-render block failed to parse")
    }
  }
  return null
}

type ElementPath = Array<string | number>

function collectNestedElementPaths(
  record: Record<string, unknown>,
  path: ElementPath,
  collector: (elements: DashboardElement[], parentPath: ElementPath) => void
): boolean {
  let foundNestedContainer = false
  if (Array.isArray(record.children)) {
    foundNestedContainer = true
    collector(record.children as DashboardElement[], [...path, "children"])
  }
  if (Array.isArray(record.tabs)) {
    foundNestedContainer = true
    record.tabs.forEach((tab, tabIndex) => {
      const children = (tab as Record<string, unknown>).children
      if (Array.isArray(children)) {
        collector(children as DashboardElement[], [...path, "tabs", tabIndex, "children"])
      }
    })
  }
  if (Array.isArray(record.items)) {
    foundNestedContainer = true
    record.items.forEach((item, itemIndex) => {
      const children = (item as Record<string, unknown>).children
      if (Array.isArray(children)) {
        collector(children as DashboardElement[], [...path, "items", itemIndex, "children"])
      }
    })
  }
  return foundNestedContainer
}

function collectTitledElementPaths(
  elements: DashboardElement[],
  parentPath: ElementPath = []
): Array<{ path: ElementPath; title: string }> {
  const matches: Array<{ path: ElementPath; title: string }> = []
  elements.forEach((element, index) => {
    const path = [...parentPath, index]
    const record = element as Record<string, unknown>
    if (typeof record.title === "string" && record.title.trim()) {
      matches.push({ path, title: record.title.trim() })
    }
    collectNestedElementPaths(record, path, (children, childPath) => {
      matches.push(...collectTitledElementPaths(children, childPath))
    })
  })
  return matches
}

function collectLeafElementPaths(
  elements: DashboardElement[],
  parentPath: ElementPath = []
): ElementPath[] {
  const matches: ElementPath[] = []
  elements.forEach((element, index) => {
    const path = [...parentPath, index]
    const record = element as Record<string, unknown>
    const nested = collectNestedElementPaths(record, path, (children, childPath) => {
      matches.push(...collectLeafElementPaths(children, childPath))
    })
    if (!nested) matches.push(path)
  })
  return matches
}

function replaceValueAtPath(
  value: unknown,
  path: ElementPath,
  replacement: DashboardElement
): unknown {
  if (path.length === 0) return replacement
  const [segment, ...rest] = path
  if (Array.isArray(value) && typeof segment === "number") {
    return value.map((item, index) =>
      index === segment ? replaceValueAtPath(item, rest, replacement) : item
    )
  }
  if (value && typeof value === "object" && typeof segment === "string") {
    const record = value as Record<string, unknown>
    if (!(segment in record)) return value
    return { ...record, [segment]: replaceValueAtPath(record[segment], rest, replacement) }
  }
  return value
}

/**
 * Recover an edit where the model returned one complete tile instead of a
 * root dashboard spec. Ambiguous targets remain rejected.
 */
export function extractAndApplyTileRender(
  markdown: string,
  currentSpec: DashboardSpec,
  userQuestion = ""
): DashboardSpec | null {
  const blocks = [
    ...extractCodeBlocks(markdown, "yaml-render"),
    ...extractCodeBlocks(markdown, "json-render"),
  ]
  if (blocks.length === 0) return null

  let replacement: DashboardElement | null = null
  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    try {
      const parsed: unknown = YAML.parse(blocks[index])
      const result = dashboardElementSchema.safeParse(parsed)
      if (result.success) {
        replacement = result.data as DashboardElement
        break
      }
    } catch {
      // Other response formats still get a chance in the caller.
    }
  }
  if (!replacement) return null

  const titledPaths = collectTitledElementPaths(currentSpec.children)
  const replacementTitle = (replacement as Record<string, unknown>).title
  const normalizedReplacementTitle =
    typeof replacementTitle === "string" ? replacementTitle.trim().toLocaleLowerCase() : ""
  let candidates = normalizedReplacementTitle
    ? titledPaths.filter(({ title }) => title.toLocaleLowerCase() === normalizedReplacementTitle)
    : []

  if (candidates.length !== 1) {
    const quotedTitles = Array.from(userQuestion.matchAll(/["']([^"']{1,160})["']/g)).map((match) =>
      match[1].trim().toLocaleLowerCase()
    )
    candidates = titledPaths.filter(({ title }) => quotedTitles.includes(title.toLocaleLowerCase()))
  }
  if (candidates.length !== 1) {
    const leafPaths = collectLeafElementPaths(currentSpec.children)
    if (leafPaths.length === 1) candidates = [{ path: leafPaths[0], title: "" }]
  }
  if (candidates.length !== 1) return null

  const nextSpec: DashboardSpec = {
    ...currentSpec,
    children: replaceValueAtPath(
      currentSpec.children,
      candidates[0].path,
      replacement
    ) as DashboardElement[],
  }
  const result = dashboardSpecSchema.safeParse(nextSpec)
  return result.success ? (result.data as DashboardSpec) : null
}

/**
 * Extracts a yaml-edit fence from markdown, deep-merges it with the current
 * dashboard spec, and returns the merged result if valid.
 */
export function extractAndApplyYamlEdit(
  markdown: string,
  currentSpec: DashboardSpec
): DashboardSpec | null {
  const blocks = extractCodeBlocks(markdown, "yaml-edit")
  if (blocks.length === 0) return null

  // Use last yaml-edit block
  const block = blocks[blocks.length - 1]

  try {
    const patch: unknown = YAML.parse(block)
    if (!patch || typeof patch !== "object") return null

    const merged = mergeDashboardYamlEdit(
      currentSpec as unknown as Record<string, unknown>,
      patch as Record<string, unknown>
    )

    // AI edits occasionally contain an otherwise-valid dashboard plus a
    // component type the catalog does not know. Run the same deterministic
    // repair used by the editor before rejecting the whole edit.
    const repaired = applyAllAutoFixableSpecIssues(merged as DashboardSpec)
    const result = dashboardSpecSchema.safeParse(repaired.spec)
    if (result.success) {
      return result.data as DashboardSpec
    }
    // A schema miss is a normal parser fallback: the same response may carry
    // a valid yaml-patch or yaml-diff fence which the caller tries next.
    logger.debug("spec-parser: yaml-edit merged result failed schema validation")
  } catch (error) {
    logger.warn("spec-parser: yaml-edit block failed to parse", error as Error)
  }

  return null
}

// ─── JSON Patch (yaml-patch fence) ──────────────────────────────────

const STATIC_DATA_FIELDS: Partial<Record<string, "data" | "rows">> = {
  BarChart: "data",
  LineChart: "data",
  AreaChart: "data",
  PieChart: "data",
  Table: "rows",
}

function hasInlineDataBinding(value: Record<string, unknown>): boolean {
  const sql = value._sql
  const connection = value._connectionId ?? value._connection_id
  return (
    typeof sql === "string" &&
    sql.trim().length > 0 &&
    typeof connection === "string" &&
    connection.trim().length > 0
  )
}

function containsUnboundEmptyDataElement(value: unknown, inheritedBinding = false): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const element = value as Record<string, unknown>
  const isBound = inheritedBinding || hasInlineDataBinding(element)
  const type = typeof element.type === "string" ? element.type : ""
  const dataField = STATIC_DATA_FIELDS[type]
  if (
    dataField &&
    Array.isArray(element[dataField]) &&
    element[dataField].length === 0 &&
    !isBound
  ) {
    return true
  }
  const children = element.children
  return (
    Array.isArray(children) &&
    children.some((child) => containsUnboundEmptyDataElement(child, isBound))
  )
}

function isTileInsertion(
  operation: JsonPatchOperation
): operation is Extract<JsonPatchOperation, { op: "add" }> {
  if (operation.op !== "add") return false
  const segments = parseJsonPointer(operation.path)
  if (segments.length < 2 || segments.at(-2) !== "children") return false
  const index = segments.at(-1)
  return index === "-" || (typeof index === "string" && /^\d+$/.test(index))
}

function operationsContainIncompleteStaticTile(operations: JsonPatchOperation[]): boolean {
  return operations.some(
    (operation) => isTileInsertion(operation) && containsUnboundEmptyDataElement(operation.value)
  )
}

/**
 * Detect a generated tile that only contains an empty static data payload.
 * Applying such a patch makes the UI claim success while rendering "No data".
 */
export function hasIncompleteStaticTileAddition(markdown: string): boolean {
  const blocks = extractCodeBlocks(markdown, "yaml-patch")
  if (blocks.length === 0) return false
  try {
    const parsed: unknown = YAML.parse(blocks[blocks.length - 1])
    return (
      Array.isArray(parsed) && operationsContainIncompleteStaticTile(parsed as JsonPatchOperation[])
    )
  } catch {
    return false
  }
}

/**
 * Extracts a yaml-patch fence from markdown, parses it as YAML list of
 * RFC 6902 operations, and applies them to the current spec.
 */
export function extractAndApplyJsonPatch(
  markdown: string,
  currentSpec: DashboardSpec,
  userQuestion = ""
): DashboardSpec | null {
  const blocks = extractCodeBlocks(markdown, "yaml-patch")
  if (blocks.length === 0) return null

  const block = blocks[blocks.length - 1]

  try {
    const parsed: unknown = YAML.parse(block)
    if (!Array.isArray(parsed)) return null

    const operations = parsed as JsonPatchOperation[]
    if (operationsContainIncompleteStaticTile(operations)) {
      logger.warn("spec-parser: rejected unbound tile with empty static data")
      return null
    }
    let patched: DashboardSpec
    try {
      patched = applyJsonPatch(currentSpec, operations)
    } catch (error) {
      if (!(error instanceof JsonPatchError)) throw error
      const rebased = rebaseReplaceOperationsToQuotedTile(operations, currentSpec, userQuestion)
      if (!rebased) throw error
      patched = applyJsonPatch(currentSpec, rebased)
      logger.info("spec-parser: rebased stale yaml-patch path to quoted tile")
    }

    const result = dashboardSpecSchema.safeParse(patched)
    if (result.success) {
      return result.data as DashboardSpec
    }
    logger.warn("spec-parser: yaml-patch result failed schema validation")
  } catch (error) {
    if (error instanceof JsonPatchError) {
      logger.warn(`spec-parser: yaml-patch operation ${error.index} failed: ${error.message}`)
    } else {
      logger.warn("spec-parser: yaml-patch block failed to parse", error as Error)
    }
  }

  return null
}

function escapeJsonPointerSegment(segment: string): string {
  return segment.replace(/~/g, "~0").replace(/\//g, "~1")
}

function elementPathToPointer(path: ElementPath): string {
  return path.map((index) => `/children/${index}`).join("")
}

function splitTilePointer(path: string): { tilePrefix: string; suffix: string } | null {
  let segments: string[]
  try {
    segments = parseJsonPointer(path)
  } catch {
    return null
  }

  let cursor = 0
  while (
    segments[cursor] === "children" &&
    cursor + 1 < segments.length &&
    /^(0|[1-9]\d*)$/.test(segments[cursor + 1])
  ) {
    cursor += 2
  }
  if (cursor === 0 || cursor >= segments.length) return null

  return {
    tilePrefix: `/${segments.slice(0, cursor).map(escapeJsonPointerSegment).join("/")}`,
    suffix: `/${segments.slice(cursor).map(escapeJsonPointerSegment).join("/")}`,
  }
}

/**
 * Recover a replace-only patch when the model used a stale tile index but the
 * user named one unique tile. This is deliberately narrow: array mutations,
 * ambiguous titles, and operations spanning multiple tiles stay rejected.
 */
function rebaseReplaceOperationsToQuotedTile(
  operations: JsonPatchOperation[],
  currentSpec: DashboardSpec,
  userQuestion: string
): JsonPatchOperation[] | null {
  if (!userQuestion || operations.length === 0 || operations.some((op) => op.op !== "replace")) {
    return null
  }

  const quotedTitles = Array.from(userQuestion.matchAll(/["']([^"']{1,160})["']/g)).map((match) =>
    match[1].trim().toLocaleLowerCase()
  )
  const candidates = collectTitledElementPaths(currentSpec.children).filter(({ title }) =>
    quotedTitles.includes(title.toLocaleLowerCase())
  )
  if (candidates.length !== 1) return null

  const pointers = operations.map((operation) => splitTilePointer(operation.path))
  if (pointers.some((pointer) => pointer === null)) return null
  const parsedPointers = pointers as Array<{ tilePrefix: string; suffix: string }>
  const stalePrefix = parsedPointers[0].tilePrefix
  if (parsedPointers.some((pointer) => pointer.tilePrefix !== stalePrefix)) return null

  const targetPrefix = elementPathToPointer(candidates[0].path)
  return operations.map((operation, index) => ({
    ...operation,
    path: `${targetPrefix}${parsedPointers[index].suffix}`,
  }))
}

// ─── Unified Diff (yaml-diff fence) ────────────────────────────────

/**
 * Extracts a yaml-diff fence from markdown, applies the unified diff to
 * the current spec's YAML representation, and re-parses.
 */
export function extractAndApplyYamlDiff(
  markdown: string,
  currentSpec: DashboardSpec
): DashboardSpec | null {
  const blocks = extractCodeBlocks(markdown, "yaml-diff")
  if (blocks.length === 0) return null

  const block = blocks[blocks.length - 1]

  try {
    // Serialize current spec to YAML
    const currentYaml = YAML.stringify(currentSpec)

    // Apply the unified diff
    const patchedYaml = applyUnifiedDiff(currentYaml, block)

    // Re-parse the patched YAML
    const parsed: unknown = YAML.parse(patchedYaml)
    if (!parsed || typeof parsed !== "object") return null

    const result = dashboardSpecSchema.safeParse(parsed)
    if (result.success) {
      return result.data as DashboardSpec
    }
    logger.warn("spec-parser: yaml-diff result failed schema validation")
  } catch (error) {
    if (error instanceof UnifiedDiffError) {
      logger.warn(`spec-parser: yaml-diff failed: ${error.message}`)
    } else {
      logger.warn("spec-parser: yaml-diff block failed to process", error as Error)
    }
  }

  return null
}

// ─── Structural Sharing for YAML Compiler ───────────────────────────

function tryCloseJson(partial: string): string | null {
  let result = partial
  let openBraces = 0
  let openBrackets = 0
  let inString = false
  let escaped = false

  for (const char of result) {
    if (escaped) {
      escaped = false
      continue
    }
    if (char === "\\") {
      escaped = true
      continue
    }
    if (char === '"') {
      inString = !inString
      continue
    }
    if (inString) continue

    if (char === "{") openBraces++
    else if (char === "}") openBraces--
    else if (char === "[") openBrackets++
    else if (char === "]") openBrackets--
  }

  if (inString) result += '"'

  // Remove trailing comma
  result = result.replace(/,\s*$/, "")

  while (openBrackets > 0) {
    result += "]"
    openBrackets--
  }
  while (openBraces > 0) {
    result += "}"
    openBraces--
  }

  try {
    JSON.parse(result)
    return result
  } catch {
    return null
  }
}
